import React, { useState, useMemo, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import ExcelJS from 'exceljs';
import { getPersonaChipClass, getEstadoEndosoArt } from '../../../../../utils/styles';

// NUESTROS COMPONENTES DE ARQUITECTURA MODULAR COMUNES
import SortableHeader from '../../common/SortableHeader';
import PaginatorFooter from '../../common/PaginatorFooter';
import ColumnSelector from '../../common/ColumnSelector';
import TableContainer from '../../common/TableContainer';
import Icons from '../../common/Icons';

// COMPONENTES UI Y MODALES DEL PROYECTO
import FiltrosDinamicos from '../../../../ui/FiltrosDinamicos.jsx';
import AuthModal from '../../../../ui/AuthModal';
import FormularioAumentosArt from '../../../../Formularios/FormularioAumentosArt';
import BuscadorInteligente from '../../../../ui/BuscadorInteligente';

// CONTROLADOR DE NEGOCIO
import { useAumentosArtTable } from './useAumentosArtTable';

// --- HELPERS VISUALES ---
const formatCurrency = (val) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 0 }).format(val || 0);

const formatDateCorrectly = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '-';
    const year = d.getUTCFullYear();
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${day}/${month}/${year}`;
};

// --- FILA MEMOIZADA ---
const TableRow = memo(({ item, isSelected, columnVisibility, onSelect, onAction, isFullUser }) => {
    // Liberación del estado: Habilitado si posee cotización vinculada independientemente del estado del endoso
    const tieneCotizacion = Boolean(item.cotizacionVinculadaId || item.cotizacionVinculada);

    return (
        <tr 
            onClick={() => onSelect(item.id)} 
            className={`group cursor-pointer transition-all duration-75 border-b border-slate-100 
                ${isSelected ? 'bg-blue-100/60' : 'hover:bg-slate-50'}`}
        >
            {columnVisibility.fechaCarga && <td className="p-2.5 px-4 border-r border-slate-100 text-center text-xs text-slate-500 tabular-nums">{formatDateCorrectly(item.fechaCarga)}</td>}
            {columnVisibility.mesAumento && <td className="p-2.5 px-4 border-r border-slate-100 text-center text-sm font-bold text-slate-700 uppercase">{item.mesAumento}</td>}
            {columnVisibility.periodoArca && <td className="p-2.5 px-4 border-r border-slate-100 text-center text-sm font-bold text-slate-700 uppercase">{item.periodoArca}</td>}
            
            {columnVisibility.nContrato && (
                <td className="p-2.5 px-4 border-r border-slate-100 text-center">
                    <span className="bg-blue-50 text-blue-700 px-2.5 py-1 rounded-lg font-mono font-black text-xs border border-blue-100 shadow-sm uppercase">
                        #{item.nContrato || 'S/N'}
                    </span>
                </td>
            )}
            
            {columnVisibility.displayDoc && <td className="p-2.5 px-4 border-r border-slate-100 text-center text-sm font-mono text-slate-500">{item.displayDoc}</td>}
            {columnVisibility.displayNombre && <td className="p-2.5 px-4 border-r border-slate-100 text-sm font-bold text-slate-900 truncate uppercase text-left max-w-[300px]" title={item.displayNombre}>{item.displayNombre}</td>}
            
            {columnVisibility.numeroEndoso && (
                <td className="p-2.5 px-4 border-r border-slate-100 text-center">
                    {item.numeroEndoso ? (
                        <span className="bg-orange-50 text-orange-700 px-2.5 py-1 rounded-lg font-bold text-xs border border-orange-100 shadow-sm uppercase">
                            {item.numeroEndoso}
                        </span>
                    ) : '-'}
                </td>
            )}
            
            {columnVisibility.compania && <td className="p-2.5 px-4 border-r border-slate-100 text-center font-bold text-xs uppercase text-slate-500">{item.compania}</td>}
            {columnVisibility.capitas && <td className="p-2.5 px-4 border-r border-slate-100 text-center font-bold text-xs">{item.capitas}</td>}
            {columnVisibility.masaSalarial && <td className="p-2.5 px-4 border-r border-slate-100 text-center font-bold text-xs text-slate-700 tabular-nums">{formatCurrency(item.masaSalarial)}</td>}
            {columnVisibility.alicuotaActual && <td className="p-2.5 px-4 border-r border-slate-100 text-center font-black text-xs text-slate-800">{item.alicuotaActual}%</td>}
            {columnVisibility.alicuotaAumentada && <td className="p-2.5 px-4 border-r border-slate-100 text-center font-black text-xs text-slate-800">{item.alicuotaAumentada}%</td>}
            
            {columnVisibility.porcentajeAumento && (
                <td className="p-2.5 px-4 border-r border-slate-100 text-center">
                    <span className="bg-orange-50 text-orange-600 px-2 py-1 rounded-lg text-[12px] font-black border border-orange-100">{item.porcentajeAumento}%</span>
                </td>
            )}
            
            {columnVisibility.estado && (
                <td className="p-2.5 px-4 border-r border-slate-100 text-center">
                    <span className={`px-3 py-1.5 rounded-full text-[11px] font-black uppercase border ${getEstadoEndosoArt(item.estado || 'Pendiente')}`}>
                        {item.estado || 'PENDIENTE'}
                    </span>
                </td>
            )}
            
            {columnVisibility.canal && <td className="p-2.5 px-4 border-r border-slate-100 text-left text-[11px] font-bold text-slate-500 uppercase truncate" title={item.canal}>{item.canal || '-'}</td>}
            {columnVisibility.canalNombre && <td className="p-2.5 px-4 border-r border-slate-100 text-left text-[11px] font-bold text-slate-700 uppercase truncate" title={item.canalNombre}>{item.canalNombre || '-'}</td>}
            {columnVisibility.subcanalNombre && <td className="p-2.5 px-4 border-r border-slate-100 text-left text-[11px] font-bold text-slate-500 uppercase truncate" title={item.subcanalNombre}>{item.subcanalNombre || '-'}</td>}
            
            {columnVisibility.responsableEndoso && (
                <td className="p-2.5 px-4 border-r border-slate-100 text-center">
                     {item.responsableEndoso ? <div className={`flex items-center gap-2 px-2.5 py-1 rounded-full w-fit mx-auto ${getPersonaChipClass(item.responsableEndoso)}`}><span className="text-xs font-bold">{item.responsableEndoso}</span></div> : '-'}
                </td>
            )}

            {/* ACCIONES */}
            <td className="p-2.5 px-4 text-center sticky right-0 z-20 bg-white group-hover:bg-slate-50 border-l border-slate-100 shadow-[-10px_0_15px_-5px_rgba(0,0,0,0.03)]">
                <div className="flex justify-center items-center gap-1.5">
                    <button 
                        type="button" 
                        onClick={(e) => { e.stopPropagation(); onAction(item, 'VIEW_QUOTE'); }} 
                        className={`p-1 transition-all ${tieneCotizacion ? 'text-slate-500 hover:text-blue-600 opacity-100 cursor-pointer' : 'text-slate-300 opacity-30 cursor-not-allowed'}`} 
                        disabled={!tieneCotizacion} 
                        title={tieneCotizacion ? "Ver Cotización" : "Sin Cotización Vinculada"}
                    >
                        <Icons.User />
                    </button>
                    <button 
                        type="button" 
                        onClick={(e) => { e.stopPropagation(); onAction(item, 'GENERATE_QUOTE'); }} 
                        className={`p-1 transition-all active:scale-90 cursor-pointer ${tieneCotizacion ? 'text-emerald-500 hover:text-emerald-700' : 'text-blue-500 hover:text-blue-700'}`} 
                        title={tieneCotizacion ? "Editar / Ver Cotización" : "Generar Cotización"}
                    >
                        {tieneCotizacion ? <Icons.ChevronRight className="w-4 h-4 text-emerald-500" /> : <Icons.Policy />}
                    </button>
                    {isFullUser && (
                        <>
                            <button type="button" onClick={(e) => { e.stopPropagation(); onAction(item, 'EDIT'); }} className="p-1 text-slate-400 hover:text-orange-600 transition-all active:scale-90 cursor-pointer" title="Editar Endoso"><Icons.Edit /></button>
                            <button type="button" onClick={(e) => { e.stopPropagation(); onAction(item, 'DELETE'); }} className="p-1 text-slate-400 hover:text-red-600 transition-all active:scale-90 cursor-pointer" title="Eliminar"><Icons.Trash /></button>
                        </>
                    )}
                </div>
            </td>
        </tr>
    );
});
TableRow.displayName = 'TableRow';

// --- ORQUESTADOR VISUAL MÁSTER ---
const AumentosArt = () => {
    const navigate = useNavigate();

    const {
        view,
        setView,
        step,
        setStep,
        loading,
        searchResults,
        setSearchResults,
        searchPerformed,
        setSearchPerformed,
        loadingSearch,
        setLoadingSearch,
        selectedEntity,
        selectedContract,
        editingItem,
        authModalConfig,
        setAuthModalConfig,
        isFullUser,
        tableState,
        itemsTable,
        contractResults,
        handleContractSelect,
        handleBack,
        handleAction: triggerAction,
        handleDelete,
        handleCreateNew,
        handleSuccess
    } = useAumentosArtTable();

    const {
        sortConfig,
        columnVisibility,
        currentPage,
        setCurrentPage,
        selectedRowId,
        setSelectedRowId,
        filterValues,
        filteredData,
        paginatedData,
        totalPages,
        handleSort,
        toggleColumn,
        handleFilterChange
    } = tableState;

    const columnLabels = {
        fechaCarga: "F. Carga", mesAumento: "Mes Aum.", periodoArca: "Periodo ARCA", nContrato: "Contrato",
        displayDoc: "CUIT / DNI", displayNombre: "Cliente / Asegurado", numeroEndoso: "N° Endoso", compania: "Compañía",
        capitas: "Capitas", masaSalarial: "Masa Salarial", alicuotaActual: "Alic. Act.", alicuotaAumentada: "Alic. Aum.",
        porcentajeAumento: "% Aum.", estado: "Estado", canal: "Canal / Org", canalNombre: "Nombre Canal",
        subcanalNombre: "Subcanal / Asociado", responsableEndoso: "Responsable"
    };

    const handleActionCallback = useCallback((item, type) => {
        triggerAction(item, type, navigate);
    }, [triggerAction, navigate]);

    // EXPORTADOR A EXCEL DE LAS COLUMNAS DEL TABLERO DE AUMENTOS
    const handleExportExcel = useCallback(async () => {
        if (!filteredData || filteredData.length === 0) {
            alert('No hay registros filtrados para exportar.');
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Tablero Aumentos ART');

        const columns = [
            { header: 'F. Carga', key: 'fechaCarga', width: 14, type: 'text', styleType: 'black' },
            { header: 'Mes Aumento', key: 'mesAumento', width: 14, type: 'text', styleType: 'black' },
            { header: 'Periodo ARCA', key: 'periodoArca', width: 14, type: 'text', styleType: 'black' },
            { header: 'N° Contrato', key: 'nContrato', width: 14, type: 'text', styleType: 'black' },
            { header: 'CUIT / DNI', key: 'displayDoc', width: 16, type: 'text', styleType: 'black' },
            { header: 'Cliente / Asegurado', key: 'displayNombre', width: 35, type: 'text', styleType: 'black' },
            { header: 'N° Endoso', key: 'numeroEndoso', width: 14, type: 'text', styleType: 'black' },
            { header: 'Compañía', key: 'compania', width: 18, type: 'text', styleType: 'black' },
            { header: 'Cápitas', key: 'capitas', width: 10, type: 'number', styleType: 'black' },
            { header: 'Masa Salarial', key: 'masaSalarial', width: 22, type: 'currency', styleType: 'black' },
            { header: 'Alic. Actual', key: 'alicuotaActual', width: 14, type: 'percent', styleType: 'black' },
            { header: 'Alic. Aumentada', key: 'alicuotaAumentada', width: 16, type: 'percent', styleType: 'yellow' },
            { header: '% Aumento', key: 'porcentajeAumento', width: 16, type: 'percent', styleType: 'red' },
            { header: 'Estado', key: 'estado', width: 16, type: 'text', styleType: 'black' },
            { header: 'Canal / Org', key: 'canal', width: 14, type: 'text', styleType: 'green' },
            { header: 'Nombre Canal', key: 'canalNombre', width: 20, type: 'text', styleType: 'green' },
            { header: 'Subcanal / Asociado', key: 'subcanalNombre', width: 20, type: 'text', styleType: 'green' },
            { header: 'Responsable', key: 'responsableEndoso', width: 20, type: 'text', styleType: 'green' },
        ];

        worksheet.columns = columns.map(col => ({ header: col.header, key: col.key, width: col.width }));

        filteredData.forEach(item => {
            const addedRow = worksheet.addRow({
                fechaCarga: formatDateCorrectly(item.fechaCarga),
                mesAumento: item.mesAumento || '-',
                periodoArca: item.periodoArca || '-',
                nContrato: item.nContrato || '-',
                displayDoc: item.displayDoc || '-',
                displayNombre: item.displayNombre || '-',
                numeroEndoso: item.numeroEndoso || '-',
                compania: item.compania || '-',
                capitas: Number(item.capitas || 0),
                masaSalarial: Number(item.masaSalarial || 0),
                alicuotaActual: Number(item.alicuotaActual || 0) / 100,
                alicuotaAumentada: Number(item.alicuotaAumentada || 0) / 100,
                porcentajeAumento: Number(item.porcentajeAumento || 0) / 100,
                estado: item.estado || 'PENDIENTE',
                canal: item.canal || '-',
                canalNombre: item.canalNombre || '-',
                subcanalNombre: item.subcanalNombre || '-',
                responsableEndoso: item.responsableEndoso || '-'
            });

            columns.forEach((col, colIdx) => {
                const cell = addedRow.getCell(colIdx + 1);
                if (col.type === 'currency') cell.numFmt = '"$"#,##0.00';
                else if (col.type === 'percent') cell.numFmt = '0.00%';

                cell.border = {
                    top: { style: 'thin', color: { argb: 'D1D5DB' } },
                    left: { style: 'thin', color: { argb: 'D1D5DB' } },
                    bottom: { style: 'thin', color: { argb: 'D1D5DB' } },
                    right: { style: 'thin', color: { argb: 'D1D5DB' } }
                };
            });
        });

        const headerRow = worksheet.getRow(1);
        headerRow.height = 30;

        columns.forEach((col, colIdx) => {
            const cell = headerRow.getCell(colIdx + 1);
            cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };

            if (col.styleType === 'black') {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '000000' } };
                cell.font = { name: 'Arial', size: 10, bold: true, color: { argb: 'FFFFFF' } };
            } else if (col.styleType === 'yellow') {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF00' } };
                cell.font = { name: 'Arial', size: 10, bold: true, color: { argb: '000000' } };
            } else if (col.styleType === 'red') {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'CC0000' } };
                cell.font = { name: 'Arial', size: 10, bold: true, color: { argb: 'FFFFFF' } };
            } else if (col.styleType === 'green') {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D9EAD3' } };
                cell.font = { name: 'Arial', size: 10, bold: true, color: { argb: '274E13' } };
            }
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        const now = new Date();
        const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        
        anchor.href = url;
        anchor.download = `Tablero_Aumentos_ART_${dateStr}.xlsx`;
        anchor.click();
        window.URL.revokeObjectURL(url);
    }, [filteredData]);

    const filterConfig = useMemo(() => {
        const getUnique = (key, data = itemsTable) => [...new Set(data.map(a => a[key]).filter(Boolean))].sort().map(v => ({label: v, value: v}));
        const itemsFilteredByCanal = filterValues.canal?.length > 0 ? itemsTable.filter(d => filterValues.canal.includes(d.canal)) : itemsTable;
        const itemsFilteredByNombre = filterValues.canalNombre?.length > 0 ? itemsFilteredByCanal.filter(d => filterValues.canalNombre.includes(d.canalNombre)) : itemsFilteredByCanal;

        return [
            { name: 'search', label: 'Buscador General', type: 'text', placeholder: 'Cliente, CUIT, Contrato, Endoso...', className: 'w-80' },
            { name: 'fechaCarga', label: 'Fecha Carga', type: 'date-range' },
            { name: 'mesAumento', label: 'Mes Aumento', type: 'select', isMulti: true, options: getUnique('mesAumento') },
            { name: 'periodoArca', label: 'Periodo ARCA', type: 'select', isMulti: true, options: getUnique('periodoArca') },
            { name: 'compania', label: 'Compañía', type: 'select', isMulti: true, options: getUnique('compania') },
            { name: 'estado', label: 'Estado', type: 'select', isMulti: true, options: getUnique('estado') },
            { name: 'canal', label: 'Canal', type: 'select', isMulti: true, options: getUnique('canal') },
            { name: 'canalNombre', label: 'Nombre Canal', type: 'select', isMulti: true, options: getUnique('canalNombre', itemsFilteredByCanal) },
            { name: 'subcanalNombre', label: 'Subcanal', type: 'select', isMulti: true, options: getUnique('subcanalNombre', itemsFilteredByNombre) },
            { name: 'responsableEndoso', label: 'Responsable', type: 'select', isMulti: true, options: getUnique('responsableEndoso') },
        ];
    }, [itemsTable, filterValues]);

    if (loading) return (
        <div className="flex items-center justify-center h-screen bg-white text-slate-400 font-bold uppercase tracking-widest text-[10px] animate-pulse">
            Sincronizando tablero de aumentos...
        </div>
    );

    if (view === 'create' || view === 'edit') {
        return (
            <div className="animate-fade-in p-6 md:p-10 bg-slate-50 min-h-screen text-left font-['Inter']">
                <div className="max-w-5xl mx-auto">
                    <div className="flex justify-between items-center mb-6">
                        <button onClick={handleBack} className="text-slate-500 hover:text-slate-800 flex items-center gap-2 text-sm font-bold transition-colors uppercase tracking-widest cursor-pointer">← Volver al Tablero</button>
                        <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">{view === 'edit' ? 'Editar Aumento' : 'Cargar Aumento'} ART</h2>
                    </div>

                    {view === 'create' && step === 1 && (
                        <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-200 animate-fade-in text-left">
                            <h2 className="text-lg font-bold text-slate-800 mb-4 text-left">Buscar Contrato para Aumento</h2>
                            <BuscadorInteligente 
                                scope="global" 
                                onResult={(r) => { setSearchResults(r); setSearchPerformed(true); }} 
                                onLoading={(l) => setLoadingSearch(l)}
                                placeholder="Ingrese el N° de contrato o CUIT del asegurado..." 
                                autoFocus={true} 
                            />
                            {!loadingSearch && searchPerformed && (
                                <div className="space-y-4 mt-6">
                                    {contractResults.length > 0 ? (
                                        contractResults.map(contract => (
                                            <div key={contract.id} className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex justify-between items-center hover:border-blue-300 transition-all cursor-pointer group text-left" onClick={() => handleContractSelect(contract)}>
                                                <div className="flex items-center gap-4 text-left">
                                                    <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-bold uppercase">{contract.compania?.charAt(0)}</div>
                                                    <div className="text-left">
                                                        <h4 className="font-bold text-slate-800 group-hover:text-blue-700 text-left uppercase">Contrato #{contract.nContrato}</h4>
                                                        <p className="text-xs text-slate-500 font-mono text-left uppercase">{contract.compania}</p>
                                                    </div>
                                                </div>
                                                <div className="text-right flex items-center gap-4">
                                                    <div className="hidden md:block pr-4 border-r border-slate-200 text-right">
                                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1 text-right">Cuit Cliente</p>
                                                        <p className="text-xs font-mono font-bold text-slate-600 text-right">{contract.clienteCuit || 'N/A'}</p>
                                                    </div>
                                                    <span className="text-xs font-bold text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity text-left">Seleccionar</span>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center py-10 bg-slate-50 border border-dashed border-slate-200 rounded-2xl text-slate-400 text-sm italic select-none">
                                            No se encontraron contratos cargados en el sistema con esos datos.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {(step === 2 || view === 'edit') && (
                        <FormularioAumentosArt 
                            initialData={editingItem}
                            contractData={selectedContract}
                            entityData={selectedEntity}
                            onCancel={handleBack}
                            onSuccess={handleSuccess}
                        />
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-screen w-full bg-white text-slate-900 overflow-hidden antialiased font-['Inter'] text-left">
            <div className="h-1 bg-blue-600 w-full shrink-0"></div>
            
            {/* CABECERA UNIFICADA */}
            <header className="flex h-20 shrink-0 items-center justify-between border-b border-slate-200 px-8 bg-white z-[110]">
                <div className="flex items-center gap-6">
                    <button type="button" onClick={() => navigate('/CargaCotizacionArtPage')} className="p-3 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-2xl transition-all border border-transparent hover:border-blue-100 cursor-pointer"><Icons.ArrowLeft /></button>
                    <div className="flex items-center gap-4">
                        <div className="p-2.5 bg-blue-50 text-blue-600 rounded-xl border border-blue-100 shadow-sm text-left">
                            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.6"><path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                        </div>
                        <div className="text-left">
                            <h2 className="text-xl font-black text-slate-800 tracking-tight leading-tight uppercase italic text-left">Tablero de Aumentos</h2>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-none mt-1 text-left">Gestión integral de variaciones tarifarias y periodos ARCA</p>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-4 text-left">
                    <ColumnSelector 
                        columnVisibility={columnVisibility} 
                        columnLabels={columnLabels} 
                        toggleColumn={toggleColumn} 
                    />

                    {/* BOTÓN EXPORTAR A EXCEL */}
                    <button 
                        onClick={handleExportExcel} 
                        className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2.5 rounded-xl text-sm font-bold shadow-md active:scale-95 flex items-center gap-2 transition-all cursor-pointer"
                        title="Exportar registros del tablero a Excel"
                    >
                        <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zM6 20V4h7v5h5v11H6zm10.5-7L14 15.5 11.5 13 10 14.5l2.5 2.5L10 19.5l1.5 1.5 2.5-2.5 2.5 2.5 1.5-1.5-2.5-2.5 2.5-2.5-1.5-1.5z"/>
                        </svg>
                        Excel
                    </button>

                    <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-2xl p-1 pr-5 shadow-inner text-left">
                        <div className="bg-blue-600 text-white px-4 py-1.5 rounded-xl text-lg font-black shadow-md text-left">{filteredData.length}</div>
                        <span className="text-[10px] font-bold uppercase text-slate-500 text-left">Registros</span>
                    </div>

                    {isFullUser && (
                        <button onClick={handleCreateNew} className="bg-blue-600 text-white px-5 py-2.5 rounded-xl hover:bg-blue-700 text-sm font-bold shadow-lg active:scale-95 flex items-center gap-2 whitespace-nowrap text-left cursor-pointer">
                            <Icons.ChevronRight className="w-5 h-5 text-white" /> Nuevo Endoso
                        </button>
                    )}
                </div>
            </header>

            {/* FILTROS DINÁMICOS CON KEY ESTABLE */}
            <FiltrosDinamicos 
                key="aumentos-table-filters"
                config={filterConfig} 
                onFilterChange={handleFilterChange} 
                defaultValues={filterValues} 
            />

            {/* TABLA PRINCIPAL RESPONSIVE */}
            <main className="flex-grow overflow-hidden flex flex-col bg-white">
                <TableContainer minWidth="3200px">
                    <thead>
                        <tr className="bg-slate-900 text-white">
                            <SortableHeader label="F. Carga" sortKey="fechaCarga" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-24 text-center" />
                            <SortableHeader label="Mes Aum." sortKey="mesAumento" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-28 text-center" />
                            <SortableHeader label="Periodo ARCA" sortKey="periodoArca" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-28 text-center" />
                            <SortableHeader label="Contrato" sortKey="nContrato" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-center" />
                            <SortableHeader label="CUIT / DNI" sortKey="displayDoc" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-center" />
                            <SortableHeader label="Cliente / Asegurado" sortKey="displayNombre" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-72 text-left" />
                            <SortableHeader label="N° Endoso" sortKey="numeroEndoso" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-center" />
                            <SortableHeader label="Compañía" sortKey="compania" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-40 text-center" />
                            <SortableHeader label="Capitas" sortKey="capitas" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-20 text-center" />
                            <SortableHeader label="Masa Salarial" sortKey="masaSalarial" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-36 text-center" />
                            <SortableHeader label="Alic. Act." sortKey="alicuotaActual" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-28 text-center" />
                            <SortableHeader label="Alic. Aum." sortKey="alicuotaAumentada" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-28 text-center" />
                            <SortableHeader label="% Aum." sortKey="porcentajeAumento" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-24 text-center" />
                            <SortableHeader label="Estado" sortKey="estado" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-center" />
                            <SortableHeader label="Canal / Org" sortKey="canal" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-left" />
                            <SortableHeader label="Nombre Canal" sortKey="canalNombre" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-48 text-left" />
                            <SortableHeader label="Subcanal / Asociado" sortKey="subcanalNombre" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-48 text-left" />
                            <SortableHeader label="Responsable" sortKey="responsableEndoso" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-40 text-center" />
                            <th className="w-32 p-4 sticky right-0 top-0 bg-slate-900 border-b border-slate-700 z-[65] uppercase tracking-wider text-[10px] font-semibold text-center text-white shadow-[-2px_0_5px_rgba(0,0,0,0.2)]">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-left">
                        {paginatedData.length > 0 ? paginatedData.map((item) => (
                            <TableRow 
                                key={item.id} 
                                item={item} 
                                isSelected={selectedRowId === item.id} 
                                columnVisibility={columnVisibility}
                                onSelect={setSelectedRowId} 
                                onAction={handleActionCallback} 
                                isFullUser={isFullUser} 
                            />
                        )) : (
                            <tr>
                                <td colSpan="19" className="p-20 text-center text-slate-400 font-bold uppercase tracking-widest text-xs italic">No se encontraron registros en el tablero.</td>
                            </tr>
                        )}
                    </tbody>
                </TableContainer>

                {/* PIE DE PAGINACIÓN */}
                <PaginatorFooter 
                    currentPage={currentPage}
                    totalPages={totalPages}
                    currentLength={paginatedData.length}
                    totalLength={filteredData.length}
                    onPageChange={setCurrentPage}
                    label="registros"
                    accentClass="text-blue-900 bg-blue-50 border-blue-100"
                />
            </main>

            <AuthModal 
                isOpen={authModalConfig.open} 
                onClose={() => setAuthModalConfig({ open: false, type: '', item: null })} 
                onConfirm={handleDelete} 
                title="Eliminar Aumento" 
                message="¿Estás seguro que deseas borrar este endoso? Al hacerlo se eliminará automáticamente la cotización vinculada en el cliente mediante una operación atómica." 
            />
        </div>
    );
};

export default AumentosArt;